import type { CheckoutPreviewResponseDto } from './services/checkoutConfig/checkoutConfig.types';

export interface LineItem {
  grams: string;
  price: string | number;
  title: string;
  quantity: number;
  variant_id: number;
  properties?: Record<string, string>;
}

export interface Metadata {
  line_items: LineItem[];
}

export interface OfferImage {
  image: string;
  variant_id: number;
}

export interface OfferData {
  name: string;
  uuid: string;
  price: string;
  metadata: Metadata;
  offer_image: OfferImage[];
  installments: number;
  shipping_type: number;
  shipping_price: number;
  student_pays_interest: boolean;
}

export interface OfferPrices {
  billet: number;
  card: number;
  pix: number;
}

export interface OrderBumpCard {
  nanoId: string;
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  price: number;
  compareAtPrice?: number | null;
  sortOrder: number;
}

/**
 * Upsell one-click (espelha UpsellCardDto do backend).
 * Aparece como popup APÓS o cartão ser aprovado e ANTES do redirect pro thank-you.
 * Cobrança reusa o mesmo cartão (token reutilizável `pgpm_` da Pagou).
 */
export interface OneClickUpsell {
  nanoId: string;
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  price: number;
  compareAtPrice?: number | null;
  acceptButtonText: string;
  declineButtonText: string;
  sortOrder: number;
}

export type GiftTriggerType = 'value' | 'quantity' | 'always';

export interface GiftCard {
  nanoId: string;
  name: string;
  product: {
    nanoId: string;
    title: string;
    imageUrl: string | null;
    price: number | null;
  };
  triggerType: GiftTriggerType;
  triggerMinValue: number | null;
  triggerMinQuantity: number | null;
  couponCodeRequired: string | null;
}

export type GiftStatus = 'locked' | 'unlocked' | 'unlocked_needs_coupon';

export interface GiftProgressState {
  gift: GiftCard;
  status: GiftStatus;
  thresholdReached: boolean;
  progress: number;
  remainingValue: number | null;
  remainingQuantity: number | null;
}

export interface AdditionalCosts {
  order_bump_total: number;
  total: number;
}

export interface DiscountBreakdown {
  couponDiscount: number;
  paymentMethodDiscount: number;
  progressiveDiscount?: number;
  totalDiscount: number;
  basePrice: number;
  finalPrice: number;
  paymentMethod?: string;
  orderBumpTotal?: number;
}

export interface InstallmentOption {
  installments: number;
  amount: number;
  interest_rate: number;
  total_amount: number;
  has_interest: boolean;
}

export interface InstallmentInfo {
  installments: number;
  installment_amount: number;
  cash_price: number;
  has_interest: boolean;
  available_installments?: InstallmentOption[];
}

// ===== Pricing canônico (Etapa 2) — espelha CanonicalPricing do backend =====
// Fonte única do preço: o front lê tudo daqui (resumo, seletor de método, parcelas, analytics).
export type PricingMethodKey = 'card' | 'pix' | 'billet' | 'apple_pay' | 'google_pay';

export interface MethodInstallments {
  installments: number;
  installment_amount: number;
  cash_price: number;
  total_with_interest: number;
  has_interest: boolean;
  available_installments: InstallmentOption[];
}

export interface MethodPricing {
  total: number;
  paymentMethodDiscount: number;
  installments?: MethodInstallments;
}

export interface CanonicalPricing {
  productsSubtotal: number;
  shipping: number;
  progressiveDiscount: number;
  couponDiscount: number;
  paymentMethodDiscount: number;
  totalDiscounts: number;
  orderBumpTotal: number;
  subtotalWithDiscount: number;
  /** O que o cliente paga (método em `paymentMethod`, com bump). */
  total: number;
  paymentMethod?: PricingMethodKey | string;
  byMethod: Partial<Record<PricingMethodKey, MethodPricing>>;
}

export interface OfferDetails {
  name: string;
  alternative_name: string | null;
}

export interface PaymentInfo {
  type: string;
  methods: string[];
  installments: number;
  plans: any[];
  student_pays_interest: boolean;
  creditCardPrimaryGateway?: string;
  creditCardGatewayRequires3DS?: boolean;
  stripePublishableKey?: string;
  pagouPublicKey?: string;
  pagouProduction?: boolean;
  /** Public Key Pagar.me — tokenização client-side (nunca secretKey). */
  pagarmePublicKey?: string;
  wallets?: {
    provider?: string;
    publishableKey?: string;
  };
}

export interface CheckoutStyle {
  hex_color: string;
  sidebar_picture: string;
  header_picture: string;
  header_picture_mobile: string;
  favicon: string | null;
}

export interface ProductInfo {
  id: number;
  name: string;
  description: string;
  type: string;
  content_delivery: string;
  cover: string;
  sales_page_url: string;
  support_email: string;
  support_whatsapp: string;
  logo: string;
}

export type UnifiedItemSource = 'offer' | 'order_bump' | 'gift' | 'upsell';

/**
 * Item canônico do checkout (espelha UnifiedItemDataDto do backend).
 * Lista única com `source` identificando a origem de cada linha — produtos da oferta,
 * order bumps aceitos, brindes e upsells.
 */
export interface UnifiedItem {
  variant_id: number;
  name: string;
  quantity: number;
  price: number;
  properties?: Record<string, any>;
  source?: UnifiedItemSource;
  imageUrl?: string | null;
  grams?: number;
  nanoId?: string;
}

// @ts-ignore
export enum StoreColorThemeModeEnum {
  /** Always use the system color theme light */
  LIGHT = 'light',

  /** Always use the system color theme dark */
  DARK = 'dark',

  /** Automatically use the system color theme */
  SYSTEM = 'system',

  /** Allow the user to set a custom color theme */
  CUSTOM = 'custom',
}

export interface DesignDto {
  id: number;
  name: string;
  description?: string;
  kind: string;
  colorTheme?: string;
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  css?: string;
}

export interface StoreDto {
  /** Identificador público da loja (PK após migração nanoId). */
  nanoId: string;
  /** @deprecated legado pré-nanoId */
  id?: number;
  name: string;
  companyName?: string;
  email: string;
  homePage?: string;
  corsUrl?: string;
  phone?: string;
  cnpj?: string;
  /** Forward-compat: campo novo no backend (ver plano "Sistema de Checkouts por loja"), ainda não presente em todo ambiente. */
  whatsapp?: string;
  /** Forward-compat: idem `whatsapp`. */
  address?: string;
  active: boolean;
  shopifyStoreId?: string;
  notes?: string;
  checkoutTemplate?: any;
  creditCardDescriptor?: string;
  favicon?: string;
  logo?: string;
  headerPicture?: string;
  headerPictureMobile?: string;
  skipCheckoutRevision?: boolean;
  checkoutJsUrl?: string;
  systemColorThemeMode?: StoreColorThemeModeEnum;
  cpfOnIdentification: boolean;
  paymentMethodDiscounts?: {
    PIX?: number;
    CARD?: number;
    BOLETO?: number;
  };
  design: DesignDto;
}

export interface ShopifyOfferDto {
  name: string;
  uuid: string;
  store: StoreDto;
  price: number;
  metadata: Metadata;
  offer_image: OfferImage[];
  installments: number;
  shipping_type: number;
  shipping_price: number;
  student_pays_interest: boolean;
  id: number;
  hash: string;
  nanoId: string;
  cart: OfferData;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  storeId: string;
  quantity: number;
  original_price: number;
  prices: OfferPrices;
  hasActiveCoupon: boolean;
  autoAppliedCoupon?: any; // Auto-applied coupon for this store
  discount_breakdown?: DiscountBreakdown;
  /** Pricing canônico (Etapa 2) — fonte única. Preferir sobre price/prices/discount_breakdown.finalPrice. */
  pricing?: CanonicalPricing;
  installment_info?: InstallmentInfo;
  offer: OfferDetails;
  discounts: OfferPrices;
  require_address: boolean;
  payment: PaymentInfo;
  checkout: CheckoutStyle;
  product: ProductInfo;
  pixels: Record<string, any>;
  full_name: string | null;
  verified_id: boolean;
  terms: boolean;
  url_terms: boolean | string | null;
  upsell: boolean;
  order_bumps: OrderBumpCard[];
  /** Upsell one-click opcional — popup pós-aprovação do cartão (Pagou). */
  oneClickUpsell?: OneClickUpsell | null;
  gifts?: GiftCard[];
  additional_costs: AdditionalCosts;
  /**
   * Lista canônica de itens do checkout (oferta + bumps aceitos), etiquetados por `source`.
   * Fonte única que substitui as listas paralelas legadas.
   */
  items?: UnifiedItem[];
  /** UTM first/last da criação da oferta (seed). */
  tracking?: {
    first?: Record<string, string>;
    last?: Record<string, string>;
  };
  /**
   * Tema de checkout publicado da loja (`store_checkouts`) — fonte única do visual
   * do checkout (header/summary/footer/general/steps). Sempre presente na resposta
   * de `GET /checkout/offer/:uuid`.
   */
  checkoutTheme: CheckoutPreviewResponseDto;
}